import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Dialog } from '@headlessui/react';
import { Home, Check, X, AlertTriangle, Building, User as UserIcon, Mail } from 'lucide-react';
import { H1, H2 } from '../../components/typography';
import { useToast } from '../../components/ui/ToastProvider';

export default function HyriaApartmentClaims() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, claim: null });
  const [rejectReason, setRejectReason] = useState('');
  const toast = useToast();

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      const housingSnap = await getDocs(query(
        collection(db, 'housing'),
        where('ownershipStatus', '==', 'PENDING_APPROVAL')
      ));

      const associationCache = new Map();
      const userCache = new Map();

      const enriched = await Promise.all(housingSnap.docs.map(async (d) => {
        const h = { id: d.id, ...d.data() };

        // Hämta claimer
        let claimerName = 'Okänd användare';
        let claimerEmail = '';
        if (h.claimedBy) {
          if (!userCache.has(h.claimedBy)) {
            const userDoc = await getDoc(doc(db, 'users', h.claimedBy));
            userCache.set(h.claimedBy, userDoc.exists() ? userDoc.data() : null);
          }
          const u = userCache.get(h.claimedBy);
          if (u) {
            claimerName = `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Okänd användare';
            claimerEmail = u.email || '';
          }
        }

        // Hämta förening
        let associationName = h.housingCooperative || 'Okänd förening';
        if (h.associationId) {
          if (!associationCache.has(h.associationId)) {
            const aDoc = await getDoc(doc(db, 'associations', h.associationId));
            associationCache.set(h.associationId, aDoc.exists() ? aDoc.data().name : null);
          }
          associationName = associationCache.get(h.associationId) || associationName;
        }

        return { ...h, claimerName, claimerEmail, associationName };
      }));

      // Sortera nyaste först
      enriched.sort((a, b) => (b.claimedAt || '').localeCompare(a.claimedAt || ''));
      setClaims(enriched);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('fetchClaims error', err);
      toast({ type: 'error', message: 'Kunde inte hämta lägenhets-claims' });
    } finally {
      setLoading(false);
    }
  };

  const callFn = (name) => httpsCallable(getFunctions(undefined, 'us-central1'), name);

  const handleApprove = async (claim) => {
    setActionLoading(true);
    try {
      await callFn('approveApartmentClaim')({ housingId: claim.id });
      toast({ type: 'success', message: `Godkänd: ${claim.address} Lgh ${claim.apartmentNumber}` });
      await fetchClaims();
    } catch (err) {
      toast({ type: 'error', message: err.message || 'Kunde inte godkänna' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.claim) return;
    const trimmedReason = rejectReason.trim();
    if (!trimmedReason) {
      toast({ type: 'error', message: 'Anledning krävs för att avvisa ett claim' });
      return;
    }
    setActionLoading(true);
    try {
      await callFn('rejectApartmentClaim')({
        housingId: rejectModal.claim.id,
        reason: trimmedReason,
      });
      toast({ type: 'success', message: 'Claim avvisad' });
      setRejectModal({ open: false, claim: null });
      setRejectReason('');
      await fetchClaims();
    } catch (err) {
      toast({ type: 'error', message: err.message || 'Kunde inte avvisa' });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64"></div>
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-gray-200 rounded"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <H1 className="flex items-center gap-2">
          <Home className="w-6 h-6 text-[#62B8E9]" />
          Lägenhets-claims att granska
        </H1>
        <p className="text-zinc-500 mt-1 text-sm">
          Användare som lagt till en lägenhet manuellt och som inte automatiskt kunnat
          matchas mot medlemsregistret. Granska och godkänn eller avvisa.
        </p>
      </div>

      {claims.length === 0 ? (
        <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center">
          <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <H2 className="mb-1">Inga claims att granska</H2>
          <p className="text-zinc-500 text-sm">
            Alla manuellt-tillagda lägenheter har antingen auto-godkänts eller redan granskats.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {claims.map((claim) => {
            const members = Array.isArray(claim.members) ? claim.members : [];
            const claimedDate = claim.claimedAt ? new Date(claim.claimedAt).toLocaleDateString('sv-SE') : '—';
            return (
              <div key={claim.id} className="bg-white rounded-2xl border border-zinc-200 p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Home className="w-4 h-4 text-[#62B8E9]" />
                      <span className="text-base font-semibold text-zinc-900">
                        {claim.address || 'Okänd adress'} · Lgh {claim.apartmentNumber || '—'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-500">
                      <Building className="w-3.5 h-3.5" />
                      <span>{claim.associationName}</span>
                      <span className="text-zinc-300">·</span>
                      <span>Begärd {claimedDate}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      disabled={actionLoading}
                      onClick={() => handleApprove(claim)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <Check className="w-4 h-4" /> Godkänn
                    </button>
                    <button
                      disabled={actionLoading}
                      onClick={() => setRejectModal({ open: true, claim })}
                      className="px-4 py-2 border border-red-200 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
                    >
                      <X className="w-4 h-4" /> Avvisa
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-zinc-50 rounded-lg p-3">
                    <div className="text-[10px] uppercase font-semibold text-zinc-400 tracking-wider mb-1.5 flex items-center gap-1.5">
                      <UserIcon className="w-3 h-3" />
                      Claim från användare
                    </div>
                    <div className="text-sm font-medium text-zinc-900">{claim.claimerName}</div>
                    {claim.claimerEmail && (
                      <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                        <Mail className="w-3 h-3" />
                        {claim.claimerEmail}
                      </div>
                    )}
                  </div>

                  <div className="bg-zinc-50 rounded-lg p-3">
                    <div className="text-[10px] uppercase font-semibold text-zinc-400 tracking-wider mb-1.5 flex items-center gap-1.5">
                      <Building className="w-3 h-3" />
                      Medlemsregister ({members.length} {members.length === 1 ? 'medlem' : 'medlemmar'})
                    </div>
                    {members.length === 0 ? (
                      <div className="text-xs text-zinc-500 italic">Inga registrerade medlemmar — verifiera annat sätt</div>
                    ) : (
                      <ul className="text-sm text-zinc-700 space-y-0.5">
                        {members.slice(0, 5).map((m, i) => {
                          const name = m.legalEntity || `${m.firstName || ''} ${m.lastName || ''}`.trim() || '—';
                          return <li key={i}>· {name}</li>;
                        })}
                        {members.length > 5 && (
                          <li className="text-zinc-400 italic">… och {members.length - 5} till</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>

                {members.length > 0 && claim.claimerName !== 'Okänd användare' && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span>
                      Auto-match misslyckades — granska att claim-namnet stämmer med en av medlemmarna ovan
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reject modal */}
      <Dialog
        open={rejectModal.open}
        onClose={() => !actionLoading && setRejectModal({ open: false, claim: null })}
        className="fixed z-50 inset-0 flex items-center justify-center p-4"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <Dialog.Panel className="relative bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
          <Dialog.Title className="text-lg font-bold text-zinc-900 mb-2">
            Avvisa lägenhets-claim
          </Dialog.Title>
          <p className="text-sm text-zinc-500 mb-4">
            Lägenheten tas bort från användarens profil och de får ett email med anledningen.
          </p>
          <label className="block text-sm font-medium text-zinc-700 mb-2">
            Anledning <span className="text-red-500 font-normal">*</span>
          </label>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="T.ex. namnet stämmer inte överens med medlemsregistret"
            rows={3}
            disabled={actionLoading}
            className="w-full bg-white border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9] resize-none"
          />
          <p className="text-xs text-zinc-500 mt-1">
            Anledningen skickas till användaren i avvisningsmejlet — fyll i tydligt.
          </p>
          <div className="flex gap-3 mt-5">
            <button
              onClick={() => { setRejectModal({ open: false, claim: null }); setRejectReason(''); }}
              disabled={actionLoading}
              className="flex-1 px-4 py-2.5 border border-zinc-300 text-zinc-700 rounded-lg font-medium hover:bg-zinc-50 disabled:opacity-50"
            >
              Avbryt
            </button>
            <button
              onClick={handleReject}
              disabled={actionLoading || !rejectReason.trim()}
              className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading ? 'Avvisar...' : 'Avvisa permanent'}
            </button>
          </div>
        </Dialog.Panel>
      </Dialog>
    </div>
  );
}
